import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { fetchCareerData } from "./careerService";
import { CareerDetailModal } from "./CareerDetailModal";
import { metricValue, metricEntry, bySource, isEmpty, fmtDate } from "./careerUtils";

/**
 * Vista del material de desempeño (carrera) sobre el colaborador.
 * Se consulta en TIEMPO REAL cada vez que se despliega el toggle (active=true);
 * no se persiste. Las tarjetas muestran info resumida y, para 4 de ellas, un
 * "Ver detalle" abre una modal con el resto de la información.
 */

// Texto truncado a N líneas
const ClampText = ({ children, lines = 2 }) => (
  <Typography
    fontSize={12}
    color="oxfordBlue.main"
    sx={{ display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }}
  >
    {children}
  </Typography>
);

// Fila label (bold) + valor
const Field = ({ label, value, clamp }) => {
  if (isEmpty(value)) return null;
  return (
    <Box>
      <Typography fontSize={11} fontWeight={700} color="secondary">{label}</Typography>
      {clamp
        ? <ClampText>{value}</ClampText>
        : <Typography fontSize={12} color="oxfordBlue.main">{value}</Typography>}
    </Box>
  );
};

// Fila "Razón: x" + "Último en y" (estilo del diseño)
const ReasonRow = ({ lead, reason, lastOn, t }) => (
  <Box>
    {!isEmpty(lead) && (
      <Typography fontSize={18} fontWeight={700} color="oxfordBlue.main" lineHeight={1.2}>
        {lead}
      </Typography>
    )}
    {!isEmpty(reason) && (
      <Typography fontSize={12} color="oxfordBlue.main">
        <b>{t("involved.career_field_reason")}:</b> {reason}
      </Typography>
    )}
    {!isEmpty(lastOn) && (
      <Typography fontSize={11} color="secondary">
        {t("involved.career_field_last_on")} {lastOn}
      </Typography>
    )}
  </Box>
);

export const CareerDetail = ({ involved, active }) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // El endpoint info_usuario identifica la consulta con username (del colaborador)
  // + legajo, que corresponde al ID del reporte (param de ruta).
  const { id: reportId } = useParams();
  const username = involved?.username;
  const legajo = reportId;

  const [career, setCareer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // null | "not_found" | "api_error"
  const [modalType, setModalType] = useState(null);

  // Consulta en tiempo real en cada apertura del toggle
  useEffect(() => {
    if (!active) return;
    if (!username || !legajo) { setError("not_found"); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCareerData({ username, legajo })
      .then((res) => { if (!cancelled) setCareer(res); })
      .catch((err) => { if (!cancelled) setError(err?.code === "not_found" ? "not_found" : "api_error"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, username, legajo]);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1.5} py={4}>
        <CircularProgress size={28} />
        <Typography fontSize={12} color="secondary">{t("involved.career_loading")}</Typography>
      </Stack>
    );
  }

  if (error) {
    const msg = error === "not_found"
      ? t("involved.career_user_not_found")
      : t("involved.career_error");
    return (
      <Typography fontSize={12} color="error" fontStyle="italic">{msg}</Typography>
    );
  }

  const data = career?.data || [];

  // ── Performance Review ──────────────────────────────────────────────
  const perfWhat = metricValue(data, "PERFORMANCE_WHAT_LAST_YEAR");
  const perfHow = metricValue(data, "PERFORMANCE_HOW_LAST_YEAR");
  const perfYear = metricEntry(data, "PERFORMANCE_WHAT_LAST_YEAR")?.YEAR
    || metricEntry(data, "PERFORMANCE_HOW_LAST_YEAR")?.YEAR;
  const perfValue = [perfWhat, perfHow].filter((x) => !isEmpty(x)).join(" | ");

  // ── Improvement Plan ────────────────────────────────────────────────
  const pip = career?.pip_detail || [];
  const pipStart = metricValue(pip, "START_DATE_IMPROVEMENT_PLAN");
  const pipEnd = metricValue(pip, "END_DATE_IMPROVEMENT_PLAN");
  const pipDesc = metricValue(pip, "DESCRIPTION_IMPROVEMENT_PLAN");

  // ── Medidas disciplinarias ──────────────────────────────────────────
  const disciplinary = bySource(data, "LK_PE_DISCIPLINARY_MEASURES");

  // ── Feedback 360 ────────────────────────────────────────────────────
  // Puede venir con múltiples entradas; la tarjeta resumen muestra la más
  // reciente por fecha de respuesta (sin depender del orden del backend).
  const feedback360 = bySource(data, "LK_PE_FEEDBACK_360");
  const lastFeedback = feedback360
    .slice()
    .sort((a, b) => (Date.parse(b?.ANSWER_DATE) || 0) - (Date.parse(a?.ANSWER_DATE) || 0))[0];

  // ── Turnover ────────────────────────────────────────────────────────
  const exitHistory = career?.exit_history || [];

  // ── Surveys ─────────────────────────────────────────────────────────
  const engAnnual = metricValue(data, "CURRENT_YEAR_ANNUAL_ENG");
  const exeAnnual = metricValue(data, "CURRENT_YEAR_ANNUAL_EXE");
  const engCheck = metricValue(data, "PREVIOUS_ONE_YEAR_ANNUAL_ENG");
  const exeCheck = metricValue(data, "PREVIOUS_ONE_YEAR_ANNUAL_EXE");

  const cards = [
    {
      key: "performance_review",
      title: t("involved.career_performance_review"),
      hasData: !isEmpty(perfValue) || !isEmpty(perfYear),
      content: (
        <Stack spacing={0.5}>
          {!isEmpty(perfYear) && (
            <Typography fontSize={16} fontWeight={700} color="oxfordBlue.main">{perfYear}</Typography>
          )}
          {!isEmpty(perfValue) && (
            <Typography fontSize={12} color="oxfordBlue.main">{perfValue}</Typography>
          )}
        </Stack>
      ),
    },
    {
      key: "improvement_plan",
      title: t("involved.career_improvement_plan"),
      hasData: pip.length > 0,
      detailType: "improvement",
      content: (
        <Stack spacing={1.2}>
          <Field label={t("involved.career_field_pip_start")} value={fmtDate(pipStart)} />
          <Field label={t("involved.career_field_pip_end")} value={fmtDate(pipEnd)} />
          <Field label={t("involved.career_field_pip_description")} value={pipDesc} clamp />
        </Stack>
      ),
    },
    {
      key: "disciplinary_measures",
      title: t("involved.career_disciplinary_measures"),
      hasData: disciplinary.length > 0,
      detailType: "disciplinary",
      content: (
        <Stack spacing={1.4} divider={<Box borderTop={`1px solid ${theme.palette.blueGray200.main}`} />}>
          {disciplinary.map((m, i) => (
            <ReasonRow key={i} lead={m?.QUANTITY_DAYS} reason={m?.REASON} lastOn={fmtDate(m?.END_DATE)} t={t} />
          ))}
        </Stack>
      ),
    },
    {
      key: "turnover",
      title: t("involved.career_turnover"),
      hasData: exitHistory.length > 0,
      detailType: "turnover",
      content: (
        <Stack spacing={1.4} divider={<Box borderTop={`1px solid ${theme.palette.blueGray200.main}`} />}>
          {exitHistory.map((e, i) => (
            <ReasonRow key={i} lead={e?.TURNOVER_TYPE} reason={e?.EXIT_REASON} lastOn={fmtDate(e?.LAST_WORKING_DATE)} t={t} />
          ))}
        </Stack>
      ),
    },
    {
      key: "feedback_360",
      title: t("involved.career_feedback_360"),
      hasData: feedback360.length > 0,
      detailType: "feedback",
      content: (
        <Stack spacing={1.2}>
          <Field label={t("involved.career_field_status")} value={lastFeedback?.STATUS} />
          <Field label={t("involved.career_field_answer_date")} value={fmtDate(lastFeedback?.ANSWER_DATE)} />
          <Field label={t("involved.career_field_strengths")} value={lastFeedback?.STRENGTHS} clamp />
          <Field label={t("involved.career_field_opportunities")} value={lastFeedback?.OPPORTUNITIES} clamp />
        </Stack>
      ),
    },
    {
      key: "surveys",
      title: t("involved.career_surveys"),
      hasData: !isEmpty(engCheck) || !isEmpty(engAnnual) || !isEmpty(exeCheck) || !isEmpty(exeAnnual),
      content: (
        <Stack spacing={1.2}>
          <Field label={t("involved.career_field_eng_check")} value={engCheck} />
          <Field label={t("involved.career_field_eng_annual")} value={engAnnual} />
          <Field label={t("involved.career_field_exe_check")} value={exeCheck} />
          <Field label={t("involved.career_field_exe_annual")} value={exeAnnual} />
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ columnGap: 2, columnCount: { xs: 1, sm: 1, md: 3 } }}>
        {cards.map((card) => (
          <Box
            key={card.key}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "12px 16px",
              border: `1px solid ${theme.palette.blueGray200.main}`,
              breakInside: "avoid",
              marginBottom: 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={1.5}
              pb={1}
              sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}
            >
              <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main">{card.title}</Typography>
            </Stack>

            {card.hasData ? card.content : (
              <Typography fontSize={12} color="secondary" fontStyle="italic">{t("involved.career_section_empty")}</Typography>
            )}

            {/* Ver detalle (solo tarjetas con modal y con datos) */}
            {card.detailType && card.hasData && (
              <Box
                onClick={() => setModalType(card.detailType)}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  cursor: "pointer",
                  mt: 1.5,
                  mx: "-16px",
                  px: "16px",
                  pt: 1,
                  borderTop: `1px solid ${theme.palette.blueGray200.main}`,
                }}
              >
                <Typography fontSize={12} fontWeight={600} color="primary">
                  {t("involved.career_view_detail")}
                </Typography>
                <Icon name="arrow_right_2" size={16} color={theme.palette.primary.main} />
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <CareerDetailModal
        open={Boolean(modalType)}
        type={modalType}
        career={career}
        onClose={() => setModalType(null)}
      />
    </>
  );
};
